import type { StyleSpecification } from "maplibre-gl";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import * as React from "react";
import { useFeature } from "store/selectors/hooks";
import { reportError } from "util/error-tracking";
import {
    BASEMAP_MANIFEST_URL,
    BasemapManifest,
    buildSelfHostedMapStyle,
    MAPTILER_STYLE_URL,
    parseBasemapManifest,
} from "./map-style";

let pmtilesProtocolRegistered = false;

const ensurePmtilesProtocol = (): void => {
    if (pmtilesProtocolRegistered) {
        return;
    }
    maplibregl.addProtocol("pmtiles", new Protocol().tile);
    pmtilesProtocolRegistered = true;
};

const fetchBasemapManifest = async (): Promise<BasemapManifest> => {
    const response = await fetch(BASEMAP_MANIFEST_URL);
    if (!response.ok) {
        throw new Error(`Basemap manifest request failed: ${response.status}`);
    }
    return parseBasemapManifest(await response.json());
};

let manifestPromise: Promise<BasemapManifest> | null = null;

const loadBasemapManifest = (): Promise<BasemapManifest> => {
    if (!manifestPromise) {
        manifestPromise = fetchBasemapManifest().catch((e: Error) => {
            manifestPromise = null;
            throw e;
        });
    }
    return manifestPromise;
};

export const useMapStyle = (): string | StyleSpecification => {
    const enabled = useFeature("selfHostedMapTiles");
    const [manifest, setManifest] = React.useState<BasemapManifest | null>(null);
    React.useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        let active = true;
        loadBasemapManifest()
            .then(result => {
                if (active) {
                    setManifest(result);
                }
            })
            .catch((e: Error) => reportError(e));
        return () => {
            active = false;
        };
    }, [enabled]);
    return React.useMemo(() => {
        if (!enabled || !manifest) {
            return MAPTILER_STYLE_URL;
        }
        ensurePmtilesProtocol();
        return buildSelfHostedMapStyle(manifest.tiles);
    }, [enabled, manifest]);
};
